import { AllSectionSelector } from "@/shared/selectors/AllSectionSelector";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useEffect } from "react";
import {
	Controller,
	FieldError,
	SubmitHandler,
	useForm,
} from "react-hook-form";
import { TestsBySection } from "../../../models/TestsBySection";

export type RowPayload = Pick<
	TestsBySection,
	| "TestsBySectionId"
	| "SectionId"
	| "GradeId"
	| "Pull2"
	| "Pull8"
	| "Pull505"
	| "Impact"
	| "Bend"
	| "Hardness"
	| "Comments"
>;

type FormValues = {
	sectionId: number | null;
	gradeId: number | null;
	gradeName: string | null;
	Pull2: boolean;
	Pull8: boolean;
	Pull505: boolean;
	Impact: boolean;
	Bend: boolean;
	Hardness: boolean;
	comments: string;
};

const DEFAULTS: FormValues = {
	sectionId: null,
	gradeId: null,
	gradeName: null,
	Pull2: false,
	Pull8: false,
	Pull505: false,
	Impact: false,
	Bend: false,
	Hardness: false,
	comments: "",
};

const toFormValues = (row: TestsBySection): FormValues => ({
	sectionId: row.SectionId,
	gradeId: row.GradeId,
	gradeName: row.GradeName,
	Pull2: row.Pull2,
	Pull8: row.Pull8,
	Pull505: row.Pull505,
	Impact: row.Impact,
	Bend: row.Bend,
	Hardness: row.Hardness,
	comments: row.Comments ?? "",
});

const PULL_OPTIONS: { value: "Pull2" | "Pull8" | "Pull505"; label: string }[] =
	[
		{ value: "Pull2", label: '2"' },
		{ value: "Pull8", label: '8"' },
		{ value: "Pull505", label: "505" },
	];

const OTHER_FIELDS: { field: keyof FormValues; label: string }[] = [
	{ field: "Impact", label: "Impact" },
	{ field: "Bend", label: "Bend" },
	{ field: "Hardness", label: "Hardness" },
];

type Props = {
	open: boolean;
	selectedRow?: TestsBySection | null;
	existingRows?: TestsBySection[];
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (payload: RowPayload) => void;
};

export const AddRowModal = ({
	open,
	selectedRow,
	existingRows = [],
	isSubmitting,
	onClose,
	onSubmit,
}: Props) => {
	const isEdit = !!selectedRow;

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		getValues,
		watch,
		formState: { isValid },
	} = useForm<FormValues>({ mode: "onChange", defaultValues: DEFAULTS });

	const selectedSectionId = watch("sectionId");
	const selectedGradeId = watch("gradeId");

	const pull2 = watch("Pull2");
	const pull8 = watch("Pull8");
	const pull505 = watch("Pull505");
	const pullValue: "Pull2" | "Pull8" | "Pull505" | null = pull2
		? "Pull2"
		: pull8
			? "Pull8"
			: pull505
				? "Pull505"
				: null;
	const pullEnabled = pullValue !== null;

	const sectionRows =
		selectedSectionId === null
			? []
			: existingRows.filter(
					(r) =>
						r.SectionId === selectedSectionId &&
						r.TestsBySectionId !== selectedRow?.TestsBySectionId,
				);
	const takenGradeIds = sectionRows
		.map((r) => r.GradeId)
		.filter((id): id is number => id != null);
	const allGradesTaken = sectionRows.some((r) => r.GradeId == null);

	const gradeConflict =
		selectedGradeId == null
			? allGradesTaken
			: takenGradeIds.includes(selectedGradeId);
	const gradeError: FieldError | undefined = gradeConflict
		? ({
				type: "manual",
				message:
					selectedGradeId == null
						? "This section already has an all-grades spec"
						: "This grade already has a spec for this section",
			} as FieldError)
		: undefined;

	const handlePullChange = (field: "Pull2" | "Pull8" | "Pull505") => {
		setValue("Pull2", field === "Pull2", { shouldValidate: true });
		setValue("Pull8", field === "Pull8", { shouldValidate: true });
		setValue("Pull505", field === "Pull505", { shouldValidate: true });
	};

	const handlePullToggle = (checked: boolean) => {
		if (checked) {
			handlePullChange("Pull2");
		} else {
			setValue("Pull2", false, { shouldValidate: true });
			setValue("Pull8", false, { shouldValidate: true });
			setValue("Pull505", false, { shouldValidate: true });
		}
	};

	const submit: SubmitHandler<FormValues> = (values) => {
		if (values.sectionId === null || gradeConflict) return;
		onSubmit({
			TestsBySectionId: selectedRow?.TestsBySectionId ?? null,
			SectionId: values.sectionId,
			GradeId: values.gradeId,
			Pull2: values.Pull2,
			Pull8: values.Pull8,
			Pull505: values.Pull505,
			Impact: values.Impact,
			Bend: values.Bend,
			Hardness: values.Hardness,
			Comments: values.comments.trim() ? values.comments.trim() : null,
		});
	};

	useEffect(() => {
		if (open) {
			reset(selectedRow ? toFormValues(selectedRow) : DEFAULTS);
		}
	}, [open, selectedRow]);

	return (
		<MesfModal
			open={open}
			handleClose={onClose}
			maxWidth="md"
			title={isEdit ? "Edit Section Tests" : "Add Section Tests"}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(submit)(e);
				}}
			>
				<MesfModal.Content dividers>
					<Stack spacing={1} sx={{ mt: 1 }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<Controller
									name="sectionId"
									control={control}
									rules={{
										validate: (value) =>
											value !== null || "Section is required",
									}}
									render={({ field, fieldState }) => (
										<AllSectionSelector
											value={field.value}
											onChange={(sectionId) => field.onChange(sectionId)}
											fieldError={fieldState.error}
											autoFocus
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 8 }}>
								<Controller
									name="gradeId"
									control={control}
									render={({ field }) => (
										<GradeSelector
											value={field.value}
											disabledGradeIds={takenGradeIds}
											fieldError={gradeError}
											onChange={(gradeId, gradeName) => {
												const prevGradeName = getValues("gradeName");
												const currentComment = getValues("comments").trim();
												const prevAuto = prevGradeName
													? `Grade ${prevGradeName} Only`
													: "";

												field.onChange(gradeId);
												setValue("gradeName", gradeName ?? null);

												// Adjust the comment only when it's empty or still the
												// auto-generated one, so a hand-typed comment is kept
												if (!currentComment || currentComment === prevAuto) {
													setValue(
														"comments",
														gradeName ? `Grade ${gradeName} Only` : "",
													);
												}
											}}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
						<Stack spacing={0.5} alignItems="flex-start">
							<Box>
								<FormControlLabel
									sx={{ ml: 0 }}
									control={
										<Checkbox
											size="small"
											checked={pullEnabled}
											onChange={(e) => handlePullToggle(e.target.checked)}
										/>
									}
									label="Pull"
								/>
								{pullEnabled && (
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										sx={{ ml: 3.5, mt: 0.5 }}
									>
										<Typography variant="caption" color="text.secondary">
											Length
										</Typography>
										<ToggleButtonGroup
											exclusive
											size="small"
											value={pullValue}
											onChange={(
												_e,
												value: "Pull2" | "Pull8" | "Pull505" | null,
											) => {
												// Ignore null so the length can't be deselected here;
												// clearing Pull is done via the checkbox above
												if (value) handlePullChange(value);
											}}
											sx={{
												"& .MuiToggleButton-root": {
													px: 1.5,
													py: 0.25,
													textTransform: "none",
												},
											}}
										>
											{PULL_OPTIONS.map((o) => (
												<ToggleButton key={o.value} value={o.value}>
													{o.label}
												</ToggleButton>
											))}
										</ToggleButtonGroup>
									</Stack>
								)}
							</Box>
							{OTHER_FIELDS.map(({ field, label }) => (
								<Controller
									key={field}
									name={field}
									control={control}
									render={({ field: f }) => (
										<FormControlLabel
											sx={{ ml: 0 }}
											control={
												<Checkbox
													size="small"
													checked={!!f.value}
													onChange={(e) => f.onChange(e.target.checked)}
												/>
											}
											label={label}
										/>
									)}
								/>
							))}
						</Stack>
						<Controller
							name="comments"
							control={control}
							render={({ field }) => (
								<TextField
									label="Comments"
									size="small"
									multiline
									minRows={2}
									value={field.value ?? ""}
									onChange={field.onChange}
								/>
							)}
						/>
					</Stack>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} color="inherit" disabled={isSubmitting}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={!isValid || isSubmitting || gradeConflict}
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
					>
						{isEdit ? "Save" : "Add"}
					</Button>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
